/**
 * Sortable, filterable table headings — one implementation, used by every table that has
 * them.
 *
 * Hugues, 17 Sep 2026, in three messages while reading the real screens: *"we should be
 * able to filter and sort on the GROUP, POP, PARTNER"*, *"and have a search box for the POP
 * column"*, then — after the filters were put in a bar at the top of the page — *"the issue
 * is that the filter bar is on top of the screen while the table of the open tickets is way
 * at the bottom of the page … so include the search functionality on the column
 * headers/labels themselves"*.
 *
 * ⚠ **The filter row is under the headings and always visible — not behind a funnel icon.**
 * A popover would have to escape the table's `overflow-x-auto` pane to avoid being clipped,
 * and more to the point the complaint was about a filter being one click and a long scroll
 * away. A control you can see is not one you have to find.
 *
 * ⚠ **A column filter narrows THIS TABLE, not the page.** The bar at the top filters
 * tickets and so moves every tile; these filter the rows of one table, the way a
 * spreadsheet's column filters do. That is a genuinely different operation, so the table
 * states what it is hiding — see `TableFilterSummary` — rather than quietly shortening
 * itself and letting somebody read the top row as the whole story.
 *
 * ⚠ The comparator lives in `@cocon-stats/shared` (`sortRowsBy`), so the rule that **a
 * missing value sorts last in both directions** is tested without a browser and cannot be
 * re-decided per table.
 */

import { useCallback, useMemo, useState } from "react";

import { defaultDirectionForValue, formatNumber, sortRowsBy, type SortableValue } from "@cocon-stats/shared";
import { ChevronDown, ChevronUp, ChevronsUpDown, X } from "lucide-react";

export interface SortColumn<T> {
  /** Stable identity for the column — not the label, which may be shortened. */
  key: string;
  /** What the heading reads. */
  label: string;
  /** The full wording, when `label` is an abbreviation. Shown on hover. */
  title?: string;
  /**
   * What this column sorts on. Omitted means the column is not sortable — which is honest
   * for a column of pills that have no order, and better than a heading that looks
   * clickable and does nothing.
   */
  value?: (row: T) => SortableValue;
  /**
   * What this column filters on: the text a row offers for matching. Omitted means no box
   * appears under that heading — counts are sorted, not typed into.
   */
  filter?: (row: T) => string | null;
  /**
   * A list to pick from instead of a box to type into, matched EXACTLY rather than
   * "contains" — picking Circet must not also match a company called "Circet Services".
   * Grouped, so the list can say which of its entries are which. Needs `filter` as well.
   */
  filterChoices?: ReadonlyArray<{ label: string; values: readonly string[] }>;
  align?: "left" | "right";
  /** Overrides the direction the first click applies. */
  firstClick?: "asc" | "desc";
  width?: number | string;
}

export interface TableSort {
  key: string | null;
  direction: "asc" | "desc";
}

/** What a heading row needs. Deliberately free of `T`: see SortableHead. */
export interface HeadView {
  sort: TableSort;
  toggle: (key: string) => void;
  filters: Record<string, string>;
  setFilter: (key: string, value: string) => void;
}

export interface TableView<T> extends HeadView {
  /** Filtered and sorted — what the body should render. */
  rows: T[];
  /** Before any column filter, so the table can say what it is hiding. */
  total: number;
  clearFilters: () => void;
  isFiltered: boolean;
}

/**
 * Filter, then sort, plus the state both need.
 *
 * `initialSort` decides what "unsorted" looks like: pass the key a table is naturally
 * ordered by, and clicking that column back to its default direction returns exactly the
 * order the reader started with.
 *
 * `sortRows` overrides the comparator for a table whose natural order is not alphabetical —
 * tile 4's is Ubi's group order, which is data rather than a sort function.
 */
export function useTableView<T>(
  rows: readonly T[],
  columns: readonly SortColumn<T>[],
  options: {
    initialSort?: TableSort;
    sortRows?: (rows: readonly T[], key: string, direction: "asc" | "desc") => T[];
  } = {},
): TableView<T> {
  const [sort, setSort] = useState<TableSort>(options.initialSort ?? { key: null, direction: "asc" });
  const [filters, setFilters] = useState<Record<string, string>>({});

  const toggle = useCallback(
    (key: string) => {
      setSort((current) => {
        if (current.key !== key) {
          const column = columns.find((c) => c.key === key);
          // ⚠ The first click on a count shows the biggest first, and on a name A-to-Z.
          // "Which drawings are most overdue" is what clicking that heading asks; answering
          // it with the smallest on top would need a second click every single time.
          const sample = column?.value && rows.length > 0 ? column.value(rows[0]!) : null;
          return { key, direction: column?.firstClick ?? defaultDirectionForValue(sample) };
        }
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      });
    },
    [columns, rows],
  );

  const setFilter = useCallback((key: string, value: string) => {
    setFilters((current) => {
      const next = { ...current };
      if (value.trim()) next[key] = value;
      else delete next[key];
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => setFilters({}), []);

  const filtered = useMemo(() => {
    const active = Object.entries(filters).filter(([, v]) => v.trim());
    if (active.length === 0) return [...rows];
    return rows.filter((row) =>
      active.every(([key, needle]) => {
        const column = columns.find((c) => c.key === key);
        if (!column?.filter) return true;
        if (column.filterChoices) return column.filter(row) === needle;
        // ⚠ A row with no value in that column is EXCLUDED once somebody types there. They
        // asked for rows matching what they typed; a blank cell does not match anything,
        // and including it would put "No POP" rows in the results of a POP search.
        return (column.filter(row) ?? "").toLowerCase().includes(needle.trim().toLowerCase());
      }),
    );
  }, [rows, columns, filters]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    if (options.sortRows) return options.sortRows(filtered, sort.key, sort.direction);
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.value) return filtered;
    return sortRowsBy(filtered, column.value, sort.direction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, columns, sort, options.sortRows]);

  return {
    rows: sorted,
    total: rows.length,
    sort,
    toggle,
    filters,
    setFilter,
    clearFilters,
    isFiltered: Object.values(filters).some((v) => v.trim()),
  };
}

/**
 * The `<thead>`: a heading row, then a filter row wherever a column can be typed into.
 *
 * Every sortable heading is a real button, so it is reachable by keyboard, and the filter
 * inputs are in DOM order right under the heading they belong to.
 */
export function SortableHead<T>({
  columns,
  view,
  sticky = false,
}: {
  columns: readonly SortColumn<T>[];
  view: HeadView;
  /**
   * Keeps the heading and filter rows visible while the body scrolls.
   *
   * ⚠ The whole point of the request: a filter you have to scroll away from is a filter you
   * have to scroll back to.
   */
  sticky?: boolean;
}) {
  const filterable = columns.some((c) => c.filter);

  return (
    <thead className={sticky ? "tbl-head-sticky" : undefined}>
      <tr>
        {columns.map((column) => {
          const active = view.sort.key === column.key;
          const right = column.align === "right";
          if (!column.value) {
            return (
              <th
                key={column.key}
                className={right ? "text-right" : undefined}
                style={{ width: column.width }}
                title={column.title}
              >
                {column.label}
              </th>
            );
          }
          return (
            <th
              key={column.key}
              className={right ? "text-right" : undefined}
              style={{ width: column.width }}
              // ⚠ On the TH, not on the button. `aria-sort` is only meaningful on the
              // element with the columnheader role; on a child button it announces nothing.
              aria-sort={active ? (view.sort.direction === "asc" ? "ascending" : "descending") : "none"}
            >
              <button
                type="button"
                onClick={() => view.toggle(column.key)}
                title={column.title ? `${column.title} — click to sort` : "Click to sort"}
                className={`sort-header inline-flex items-center gap-1 ${right ? "flex-row-reverse" : ""}`}
              >
                {column.label}
                {/* ⚠ An inactive column shows a faint two-way chevron rather than nothing:
                    a heading that only reveals it is sortable once you have already clicked
                    it is one nobody clicks. */}
                {active ? (
                  view.sort.direction === "asc" ? (
                    <ChevronUp className="h-3 w-3" style={{ color: "#ed125f" }} />
                  ) : (
                    <ChevronDown className="h-3 w-3" style={{ color: "#ed125f" }} />
                  )
                ) : (
                  <ChevronsUpDown className="h-3 w-3 text-slate-300" />
                )}
              </button>
            </th>
          );
        })}
      </tr>

      {filterable && (
        <tr className="tbl-filter-row">
          {columns.map((column) => (
            <th key={column.key} style={{ width: column.width }}>
              {column.filter && column.filterChoices ? (
                <ColumnChoice
                  label={column.title ?? column.label}
                  choices={column.filterChoices}
                  value={view.filters[column.key] ?? ""}
                  onChange={(next) => view.setFilter(column.key, next)}
                />
              ) : column.filter ? (
                <ColumnFilter
                  label={column.title ?? column.label}
                  value={view.filters[column.key] ?? ""}
                  onChange={(next) => view.setFilter(column.key, next)}
                />
              ) : null}
            </th>
          ))}
        </tr>
      )}
    </thead>
  );
}

/** A "contains" box under a heading. Exported for tables that filter on the server. */
export function ColumnFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Filter ${/^[A-Z0-9]+$/.test(label) ? label : label.toLowerCase()}`}
        aria-label={`Filter by ${label}`}
        className="field field-sm field-filter"
      />
      {value && (
        <button
          type="button"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          onClick={() => onChange("")}
          aria-label={`Clear the ${label} filter`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

/** A pick-list under a heading, for a column whose values are a known set. */
function ColumnChoice({
  label,
  choices,
  value,
  onChange,
}: {
  label: string;
  choices: ReadonlyArray<{ label: string; values: readonly string[] }>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={`Filter by ${label}`}
      className="field field-sm field-filter"
    >
      {/* "All", not "Filter company": a select cannot scroll its label, and in a 90 px
          column the longer one was cut to "Filter coı". */}
      <option value="">All</option>
      {choices
        .filter((group) => group.values.length > 0)
        .map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.values.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </optgroup>
        ))}
    </select>
  );
}

/**
 * What a column filter is hiding, stated where the reader is.
 *
 * ⚠ Never silent. A table that quietly shortens itself is one somebody reads as "only three
 * are overdue" — which on this product is the whole failure mode in miniature.
 */
export function TableFilterSummary<T>({ view, noun }: { view: TableView<T>; noun: string }) {
  if (!view.isFiltered) return null;
  return (
    <span className="flex items-center gap-2 text-xs">
      <span style={{ color: "#b45309" }}>
        <span className="num font-semibold">{formatNumber(view.rows.length)}</span> of{" "}
        <span className="num">{formatNumber(view.total)}</span> {noun} — the rest are filtered out
      </span>
      <button type="button" className="font-semibold" style={{ color: "#ed125f" }} onClick={view.clearFilters}>
        Clear
      </button>
    </span>
  );
}
